import { useEffect, useRef } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { type Fragment as PMFragment } from '@atlaskit/editor-prosemirror/model';

import { useInEditorDirectStreaming } from '../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { adfResponseToFragment } from '../../utils/request/adf-response';

/**
 * This component connects the AI Session State to the Editor document.
 *
 * ## 1. The Listener (ai-session-context-plugin)
 * This ProseMirror plugin acts as the "memory" for the session.
 * - It receives STREAM | COMPLETE | ERROR events and ADF chunks from the orchestrator.
 *
 * ## 2. The Bridge (ADFStreamingManager)
 * This component reacts to ai-session-context-plugin state changes.
 * - It passes the new ADF to the `useInEditorDirectStreaming` hook, which handles the actual insertion into the document.
 */
export function ADFStreamingManager(): null {
	const { api, editorView, editorPluginAIProvider, endExperience } = useContentComponentContext();
	const { status, adfResponse, configItem, aiSessionId, isShowingAcceptRejectScreen, positions } =
		useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
			status: state.aiExperienceState?.aiSessionPluginState?.status,
			adfResponse: state.aiExperienceState?.aiSessionPluginState?.adfResponse,
			configItem: state.aiExperienceState?.aiDecorationExperiencePluginState?.configItem,
			aiSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.currentSessionId,
			isShowingAcceptRejectScreen:
				state.aiExperienceState?.aiDecorationExperiencePluginState?.isShowingAcceptRejectScreen,
			positions: state.aiExperienceState?.aiDecorationExperiencePluginState?.positions,
			nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
		}));

	const isProgrammaticScroll = useRef(false);
	const lastFragmentRef = useRef<PMFragment | undefined>(undefined);
	const hasMountedRef = useRef(false);
	const alreadyCompletedRef = useRef(false);

	const { updateStream, mountDecorations } = useInEditorDirectStreaming({
		api,
		configItem: configItem || editorPluginAIProvider.baseGenerate,
		isProgrammaticScroll,
		positions: positions || [0, 0],
		view: editorView,
		aiSessionId,
	});

	useEffect(() => {
		// only call this once per stream - it can be called multiple times in a session
		// as a session can have multiple streams
		if (mountDecorations && !hasMountedRef.current && status === 'streaming') {
			hasMountedRef.current = true;
			return mountDecorations();
		}
	}, [status, mountDecorations]);

	useEffect(() => {
		if (!adfResponse || !hasMountedRef.current) {
			return;
		}

		const pmFragment = adfResponseToFragment({
			schema: editorView.state.schema,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			adfResponse: adfResponse!,
		});

		// Handle streaming updates
		if (status === 'streaming') {
			const isNewFragment = !lastFragmentRef.current || !lastFragmentRef.current.eq(pmFragment);
			alreadyCompletedRef.current = false;

			if (isNewFragment) {
				lastFragmentRef.current = pmFragment;
				isProgrammaticScroll.current = true;

				updateStream?.({ fragment: pmFragment, isFinalStream: false, hasUserScrolled: false });
			}
			return;
		}

		// Handle completion of stream
		if (status === 'complete' && !isShowingAcceptRejectScreen) {
			alreadyCompletedRef.current = true;
			hasMountedRef.current = false;
			updateStream?.({ fragment: pmFragment, isFinalStream: true, hasUserScrolled: false });
			endExperience();
		}
	});

	return null;
}
